import {useState} from "react";
import {useDispatch} from "react-redux";
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat} from "../../../../Apis/functions";
import {SearchByFilters} from "../../../../reduxToolkit/Search/SearchApi";
import CustomTh from "../../../../components/table/CustomTh";
import CustomTd from "../../../../components/table/CustomTd";
import TableFunction from "../../../../components/table/TableFunction";
import {userTypeDrop} from "../../../../enums/userTypeEnumDrop";
import CustomImageTable from "../../../../components/CustomImageTable";
import CustomTooltip from "../../../../components/CustomTooltip";
import checkIcon from "../../../../assets/icon/checkIcon.svg";
import cancelIcon from "../../../../assets/icon/cancelIcon.svg";
import {TABLES} from "../../../../Apis/Tables";
import {
    CRITERIA_MODEL,
    DEFAULT_LEFT,
    LEFT_SECOND_COLUMN_WITHOUT_CHECKBOX,
    WIDTH_COLUMN_FIXED
} from "../../../../components/table/Model/TableModel";
import {DATA_TYPE_MODEL, ORDER_MODEL, SEARCH_OPERATION_MODEL} from "../../../Modals/Model/Model";
import {fieldModelName} from "../../../../enums/fieldModelEnum";

/**
 * Rule every record satisfies, used when no advanced filter is applied.
 *
 * The advanced endpoint resolves a group with an empty rules list as no results, so the
 * filter cannot be left empty: createdAt is never null, so NOT NULL matches the whole table.
 *
 * @returns {Object} Rule ready to be folded by CRITERIA_MODEL.set.
 */
const matchAllRule = () => ({
    dataOption: SEARCH_OPERATION_MODEL.AND,
    fieldType: DATA_TYPE_MODEL.LONG,
    filterKey: fieldModelName.createdAt,
    operation: SEARCH_OPERATION_MODEL.NN,
    sort: ORDER_MODEL.ASC,
    table: TABLES.LOG_IN_LOG,
});
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
const AccessLogsTableApp = ({finalArray, modified, setModified, setBodyForExport}) =>
{
    const dispatch = useDispatch();
    const tableId = "tbl_lt_supplier_docs";
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const leftPosition = DEFAULT_LEFT;
    //Returns an array of table header elements
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head" customWidth={WIDTH_COLUMN_FIXED}>{t('name')}</CustomTh>,
            <CustomTh tableId={tableId} fixedXY={true} left={LEFT_SECOND_COLUMN_WITHOUT_CHECKBOX} customWidth={WIDTH_COLUMN_FIXED}>{t('last_name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('second_last_name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('user_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('successful_attempt')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('platform')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('date')}</CustomTh>
        ];
    }
    //Returns an object with the body components
    const getTbody = () => {
        if(!results || results?.totalElements === 0 || results?.length === 0)  return {
            body: [],
            data: []
        };

        let body =  results?.content?.map((item) => {
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        <CustomTooltip
                            text={item?.userName || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId} fixed={true} left={LEFT_SECOND_COLUMN_WITHOUT_CHECKBOX}>
                        <CustomTooltip
                            text={item?.userLastName || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId} >
                        <CustomTooltip
                            text={item?.secondLastName || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId} >
                        <CustomTooltip
                            text={t(userTypeDrop[item?.userTypeId]).toLocaleUpperCase()}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId} ><CustomImageTable text={""} image={item?.wasSuccessfully ? checkIcon : cancelIcon} classNameImage={'object-fit-contain'}/></CustomTd>
                    <CustomTd tableId={tableId} >
                        <CustomTooltip
                            text={t(item?.platform?.toLowerCase()).toLocaleUpperCase()}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId} >
                        <CustomTooltip
                            text={getDateFormat(item?.createdAt, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds) || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body: body,
            data: results?.content
        }
    };

    const getActionsButtons = () => {
        return <></>;
    }

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });

        const body = {
            pagination: {
                page: page,
                size: rowsPerPage,
            },
            searchCriteriaList: criteriaList,
        };

        // The export goes through the advanced endpoint, which reads the filter from rootGroup
        setBodyForExport({
            pagination: body.pagination,
            model: `${TABLES.LOG_IN_LOG}`,
            rootGroup: CRITERIA_MODEL.set(finalArray?.length ? finalArray : [matchAllRule()]),
        });

        dispatch(SearchByFilters({ option:`${TABLES.LOG_IN_LOG}`, body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    // Setup table functions and pagination using shared TableFunction logic
    const {t, thead, tbody, pagination, rowsPerPage} = TableFunction({
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage:'platform_access_per_page',
        hasContainer:true,
        fixedPagination:false,
        getData,//New
        modified,//New
        setModified,//New

    });

    return {
        tableId,
        thead,
        tbody,
        getActionsButtons,
        pagination
    };

}
export default AccessLogsTableApp;
